import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';
import selector from './gatesEditor.selector';

import { infoShape } from '../../types/info.types';
import { useTranslation } from "../../contexts/TranslationContext";
import { GatewayOutlined } from "@ant-design/icons";
import { getStatics } from '../../config';

import { setGates } from "../Construction/construction.reducer";
import RangeEdit from "../../components/RangeEdit/RangeEdit";

const GatesEditor = ({ collapsed }) => {
  const [isModalOpen, setModalOpen] = useState(false);

  const availableGateTypes = ['swing_gate', 'double_leaf_gate', 'sectional_gate', 'roller_gate', 'hole_gate'];
  const dispatch = useDispatch();
  const {
    width,
    gates,
    gateImg,
    loaded,
    gate_count_available,
    gate_count_selected,
    gate_count_info
  } = useSelector(selector);
  const translationsData = useTranslation();

  const images = {
    swing_gate: getStatics('gates_new/swing_gate.jpeg'),
    double_leaf_gate: getStatics('gates_new/double_leaf_gate.jpeg'),
    sectional_gate: getStatics('gates_new/sectional_gate.jpeg'),
    roller_gate: getStatics('gates_new/roller_gate.jpeg'),
  };

  // ---------- helper wspólny ----------
  const mapDictToOptions = (dict = {}) =>
    Object.keys(dict)
      .map((k) => {
        const meta = dict[k] ?? {};
        const value = Number(k);
        if (!Number.isFinite(value)) return null;
        return {
          id: String(k),
          value,
          lower: Number(meta.lower),
          higher: Number(meta.higher),
          default: Boolean(meta.default),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.value - b.value);

  const rangeOptions = (minCm, maxCm) =>
    Array.from({ length: Math.floor((maxCm - minCm) / 10) + 1 }, (_, i) => {
      const v = minCm + i * 10;
      return { id: String(v), value: v, lower: v, higher: v, default: v === minCm };
    });

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const pickBestOption = (opts, currentValue) => {
    if (!opts || opts.length === 0) return null;

    const def = opts.find((o) => o.default);
    if (def) return def;

    const cur = Number(currentValue);
    if (Number.isFinite(cur)) {
      let best = opts[0];
      let bestDiff = Math.abs(opts[0].value - cur);
      for (const o of opts) {
        const d = Math.abs(o.value - cur);
        if (d < bestDiff) {
          bestDiff = d;
          best = o;
        }
      }
      return best;
    }

    return opts[0];
  };

  // ---------- szerokości ----------
  const getWidthOptions = React.useCallback(
    (type) => {
      const dict = gates?.available_widths?.[type] ?? {};
      return mapDictToOptions(dict);
    },
    [gates?.available_widths]
  );

  // 🔹 typy bram dostępne (mają jakiekolwiek widths) + ograniczenia po szerokości garażu
  const filteredGateTypes = React.useMemo(() => {
    const garageWidth = Number(width) || 0;

    const typesWithWidths = availableGateTypes.filter((type) => {
      const opts = getWidthOptions(type);
      return opts && opts.length > 0;
    });

    if (garageWidth < 250) {
      return typesWithWidths.filter(
        (t) => t === 'swing_gate' || t === 'double_leaf_gate' || t === 'hole_gate'
      );
    }

    if (garageWidth < 300) {
      return typesWithWidths.filter(
        (t) => t === 'swing_gate' || t === 'double_leaf_gate' || t === 'roller_gate' || t === 'hole_gate'
      );
    }

    return typesWithWidths;
  }, [width, availableGateTypes, getWidthOptions]);

  // 🔹 jeśli gate ma typ spoza filteredGateTypes → zmieniamy na swing_gate
  useEffect(() => {
    if (!gates || !Array.isArray(gates.gates)) return;
    if (!filteredGateTypes || filteredGateTypes.length === 0) return;

    const allowed = new Set(filteredGateTypes);
    let changed = false;

    const fixedGates = gates.gates.map((g) => {
      if (!allowed.has(g.type)) {
        changed = true;
        return { ...g, type: 'swing_gate' };
      }
      return g;
    });

    if (changed) {
      dispatch(setGates({ ...gates, gates: fixedGates }));
    }
  }, [filteredGateTypes, gates, dispatch]);

  // ---------- REGUŁY OGRANICZEŃ WYSOKOŚCI wg TYPU + SZEROKOŚCI ----------
  const heightPolicyOK = (type, height, widthVal) => {
    const h = Number(height);
    const w = Number(widthVal);

    if (type === 'swing_gate') return h >= 200 && h <= 270 && h % 10 === 0;
    if (type === 'double_leaf_gate') return h >= 200 && h <= 400 && h % 10 === 0;
    if (type === 'roller_gate') return h >= 200 && h <= 250 && h % 10 === 0;

    if (type === 'sectional_gate') {
      if (h < 200 || h > 300 || h % 10 !== 0) return false;
      if (w >= 480 && w <= 510) return h <= 260;
      if (w >= 460 && w <= 470) return h <= 280;
      if (w >= 250 && w <= 450) return h <= 300;
      return h <= 300;
    }
    return true;
  };

  // ---------- wysokości ----------
  const getHeightOptions = React.useCallback(
    (gateOrType) => {
      const type = typeof gateOrType === 'string' ? gateOrType : gateOrType?.type;
      if (!type) return [];

      const currentWidth = typeof gateOrType === 'string' ? undefined : Number(gateOrType?.width);

      const all = gates?.available_heights ?? {};
      let dict = all?.[type];

      if (dict && typeof dict === 'object') {
        const keys = Object.keys(dict);
        const looksFlat = keys.some((k) => {
          const v = dict[k];
          return v && typeof v === 'object' && ('lower' in v || 'higher' in v || 'default' in v);
        });

        if (!looksFlat) {
          const wKey = currentWidth != null ? String(currentWidth) : '';
          if (wKey && dict[wKey] && typeof dict[wKey] === 'object') {
            dict = dict[wKey];
          } else {
            const merged = {};
            for (const wk of keys) {
              const sub = dict[wk];
              if (sub && typeof sub === 'object') {
                for (const hk of Object.keys(sub)) {
                  if (!merged[hk]) merged[hk] = sub[hk];
                }
              }
            }
            dict = merged;
          }
        }
      }

      let options = mapDictToOptions(dict || {});
      options = options.filter((o) => heightPolicyOK(type, o.value, currentWidth));

      if (options.length === 0) {
        if (type === 'swing_gate') options = rangeOptions(200, 270);
        else if (type === 'double_leaf_gate') options = rangeOptions(200, 400);
        else if (type === 'roller_gate') options = rangeOptions(200, 250);
        else if (type === 'sectional_gate') {
          options = rangeOptions(200, 300).filter((o) =>
            heightPolicyOK(type, o.value, currentWidth)
          );
        }
      }
      return options;
    },
    [gates?.available_heights]
  );

  const isReadyForWidth =
    loaded && Boolean(gates?.available_widths) && getWidthOptions('swing_gate').length > 0;

  const isReadyForHeight =
    loaded && Boolean(gates?.available_heights) && getHeightOptions('swing_gate').length > 0;

  // ---------- GRANICE POZYCJI ----------
  const getPositionBounds = (gate, garageWidth) => {
    const isEdgeRestricted = gate?.type === 'sectional_gate' || gate?.type === 'roller_gate';
    const edge = isEdgeRestricted ? 25 : 0;
    const gw = Number(garageWidth) || 0;
    const bw = Number(gate?.width || 0);
    const minPos = edge;
    const maxPos = Math.max(edge, gw - bw - edge);
    return { minPos, maxPos };
  };

  // ---------- „po otwarciu” (lower) ----------
  const pickHeightDictForGate = (gate) => {
    const type = gate?.type;
    const all = gates?.available_heights ?? {};
    let dict = all?.[type] || {};
    if (!dict || typeof dict !== 'object') return {};

    const keys = Object.keys(dict);
    const looksFlat = keys.some((k) => {
      const v = dict[k];
      return v && typeof v === 'object' && ('lower' in v || 'higher' in v || 'default' in v);
    });

    if (!looksFlat) {
      const wKey = String(gate?.width ?? '');
      if (wKey && dict[wKey] && typeof dict[wKey] === 'object') {
        dict = dict[wKey];
      } else {
        const merged = {};
        for (const wk of keys) {
          const sub = dict[wk];
          if (sub && typeof sub === 'object') {
            for (const hk of Object.keys(sub)) {
              if (!merged[hk]) merged[hk] = sub[hk];
            }
          }
        }
        dict = merged;
      }
    }
    return dict || {};
  };

  const getSelectedWidthLower = (gate) => {
    const dict = gates?.available_widths?.[gate?.type] ?? {};
    const meta = dict?.[String(gate?.width)] ?? null;
    const lower = Number(meta?.lower);
    return Number.isFinite(lower) ? lower : null;
  };

  const getSelectedHeightLower = (gate) => {
    const dict = pickHeightDictForGate(gate);
    const meta = dict?.[String(gate?.height)] ?? null;
    const lower = Number(meta?.lower);
    return Number.isFinite(lower) ? lower : null;
  };

  // 🔹 EFFECT 1: jeśli zmieniło się width / available_widths → korygujemy szerokości bram + pozycję
  useEffect(() => {
    if (!loaded) return;
    if (!gates || !Array.isArray(gates.gates)) return;

    let changed = false;

    const fixedGates = gates.gates.map((g) => {
      const opts = getWidthOptions(g.type);
      if (!opts || opts.length === 0) return g;

      const currentWidth = Number(g.width);
      const hasCurrent = opts.some((o) => o.value === currentWidth);

      if (hasCurrent) return g;

      const nextOption = pickBestOption(opts, currentWidth);
      if (!nextOption) return g;

      const { minPos, maxPos } = getPositionBounds(
        { ...g, width: nextOption.value },
        width
      );
      const nextPosition = clamp(Number(g.position || 0), minPos, maxPos);

      changed = true;
      return {
        ...g,
        width: nextOption.value,
        position: nextPosition,
      };
    });

    if (changed) {
      dispatch(setGates({ ...gates, gates: fixedGates }));
    }
  }, [loaded, width, gates?.available_widths, gates?.gates, getWidthOptions, dispatch]);

  // 🔹 EFFECT 2: po zmianie width / available_heights / po korekcie szerokości → korygujemy wysokości bram
  useEffect(() => {
    if (!loaded) return;
    if (!gates || !Array.isArray(gates.gates)) return;

    let changed = false;

    const fixedGates = gates.gates.map((g) => {
      const heightOpts = getHeightOptions(g);
      if (!heightOpts || heightOpts.length === 0) return g;

      const currentHeight = Number(g.height);
      const hasCurrent = heightOpts.some((o) => o.value === currentHeight);

      if (hasCurrent) return g;

      const nextOpt = pickBestOption(heightOpts, currentHeight);
      if (!nextOpt) return g;

      changed = true;
      return { ...g, height: nextOpt.value };
    });

    if (changed) {
      dispatch(setGates({ ...gates, gates: fixedGates }));
    }
  }, [loaded, width, gates?.available_heights, gates?.gates, getHeightOptions, dispatch]);

  useEffect(() => {
    if (!gates || !Array.isArray(gates.gates)) return;

    let changed = false;

    const fixed = gates.gates.map((g, i) => {
      if (g?.id) return g;
      changed = true;
      return { ...g, id: `gate_${i}` };
    });

    if (changed) {
      dispatch(setGates({ ...gates, gates: fixed }));
    }
  }, [gates, dispatch]);

  return (
    <EditableSection
      collapsed={collapsed}
      icon={<GatewayOutlined />}
      title={translationsData.gate_parameters || "gate_parameters"}
    >
      <SelectEdit
        loaded={loaded}
        title={translationsData.gate_number || "gate_number"}
        isEdit
        options={gates.available_count.map((el) => ({ id: el, value: el }))}
        onChange={(value) => {
          const newCount = Number(value);
          const newGates = gates.gates.slice(0, newCount);
          while (newGates.length < newCount) {
            const idx = newGates.length;
            newGates.push({
              id: `gate_${idx}`,
              type: 'swing_gate',
              width: 200,
              height: 200,
              position: idx * 10
            });
          }
          dispatch(setGates({ ...gates, count: newCount, gates: newGates }));
        }}
        selected={gates.count}
      />

      {gates.gates.map((gate, index) => {
        const widthOptions = getWidthOptions(gate.type);
        const heightOptions = getHeightOptions(gate);
        const { minPos, maxPos } = getPositionBounds(gate, width);

        const widthLower = getSelectedWidthLower(gate);
        const heightLower = getSelectedHeightLower(gate);

        return (
          <React.Fragment key={index}>
            <SelectEdit
              title={translationsData.gate_type || 'gate_type'}
              selectedValue={translationsData[gate.type] || gate.type}
              options={filteredGateTypes.map((el) => ({
                id: el,
                value: translationsData[el] || el,
              }))}
              onChange={(value) => {
                const nextType = value;

                // width dla typu
                const widthOptionsForType = getWidthOptions(nextType);
                let nextWidth = gate.width;

                if (widthOptionsForType && widthOptionsForType.length > 0) {
                  const bestW = pickBestOption(widthOptionsForType, nextWidth);
                  if (bestW) nextWidth = bestW.value;
                }

                // ✅ height zależny od (type + width)
                const heightOptionsForType = getHeightOptions({ ...gate, type: nextType, width: nextWidth });
                let nextHeight = gate.height;

                if (heightOptionsForType && heightOptionsForType.length > 0) {
                  const hasCurrent = heightOptionsForType.some((o) => o.value === Number(nextHeight));
                  if (!hasCurrent) {
                    const bestH = pickBestOption(heightOptionsForType, nextHeight);
                    if (bestH) nextHeight = bestH.value;
                  }
                }

                const bounds = getPositionBounds(
                  { ...gate, type: nextType, width: nextWidth },
                  width
                );
                const nextPosition = clamp(
                  Number(gate.position || 0),
                  bounds.minPos,
                  bounds.maxPos
                );

                const updatedGate = {
                  ...gate,
                  type: nextType,
                  width: nextWidth,
                  height: nextHeight,
                  position: nextPosition,
                };

                const updatedGates = {
                  ...gates,
                  gates: gates.gates.map((g, i) => (i === index ? updatedGate : g)),
                };
                dispatch(setGates(updatedGates));
              }}
              info={{
                info_title: translationsData[gate.type] || gate.type,
                info_content:
                  translationsData[gate.type + '_content'] ||
                  gate.type + '_content',
                img_one: images[gate.type],
              }}
            />

            <SelectEdit
              title={translationsData.gate_width || 'gate_width'}
              loaded={isReadyForWidth}
              selectedValue={gate.width}
              options={widthOptions}
              onChange={(value) => {
                const numeric = Number(value);

                // ✅ po zmianie szerokości musimy też sprawdzić, czy wysokość nadal jest legalna
                const heightOptionsAfterWidth = getHeightOptions({ ...gate, width: numeric });
                let nextHeight = gate.height;

                if (heightOptionsAfterWidth && heightOptionsAfterWidth.length > 0) {
                  const hasCurrent = heightOptionsAfterWidth.some((o) => o.value === Number(nextHeight));
                  if (!hasCurrent) {
                    const bestH = pickBestOption(heightOptionsAfterWidth, nextHeight);
                    if (bestH) nextHeight = bestH.value;
                  }
                }

                const bounds = getPositionBounds({ ...gate, width: numeric }, width);
                const nextPosition = clamp(
                  Number(gate.position || 0),
                  bounds.minPos,
                  bounds.maxPos
                );

                const updatedGate = { ...gate, width: numeric, height: nextHeight, position: nextPosition };
                const updatedGates = {
                  ...gates,
                  gates: gates.gates.map((g, i) => (i === index ? updatedGate : g)),
                };
                dispatch(setGates(updatedGates));
              }}
            />

            {/* READ-ONLY select: szerokość po otwarciu (lower) */}
            <SelectEdit
              title={translationsData.gate_width_after_opening || 'gate_width_after_opening'}
              loaded={true}
              isEdit={false}
              selectedValue={widthLower ?? '—'}
              options={widthLower != null ? [{ id: String(widthLower), value: widthLower }] : []}
            />

            <SelectEdit
              title={translationsData.gate_height || 'gate_height'}
              loaded={isReadyForHeight}
              selectedValue={gate.height}
              options={heightOptions}
              onChange={(value) => {
                const numeric = Number(value);
                const updatedGate = { ...gate, height: numeric };
                const updatedGates = {
                  ...gates,
                  gates: gates.gates.map((g, i) => (i === index ? updatedGate : g)),
                };
                dispatch(setGates(updatedGates));
              }}
            />

            {/* READ-ONLY select: wysokość po otwarciu (lower) */}
            <SelectEdit
              title={translationsData.gate_height_after_opening || 'gate_height_after_opening'}
              loaded={true}
              isEdit={false}
              selectedValue={heightLower ?? '—'}
              options={heightLower != null ? [{ id: String(heightLower), value: heightLower }] : []}
            />

            <RangeEdit
              title={translationsData.gate_position || 'gate_position'}
              min={minPos}
              step={5}
              max={maxPos}
              unit={'cm'}
              selected={clamp(Number(gate.position || 0), minPos, maxPos)}
              onChange={(value) => {
                const bounds = getPositionBounds(gate, width);
                const clamped = clamp(Number(value), bounds.minPos, bounds.maxPos);
                const updatedGate = { ...gate, position: clamped };
                const updatedGates = {
                  ...gates,
                  gates: gates.gates.map((g, i) => (i === index ? updatedGate : g)),
                };
                dispatch(setGates(updatedGates));
              }}
            />
          </React.Fragment>
        );
      })}
    </EditableSection>
  );
};

GatesEditor.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  gate_count: PropTypes.number,
  gate_count_available: PropTypes.arrayOf(PropTypes.number),
  gate_count_info: infoShape,
  gate_count_selected: PropTypes.number,
  gateImg: PropTypes.string,
  loaded: PropTypes.bool,
};

export default GatesEditor;
